// Local theme cache system for instant theme loading
import { IClientInfo } from './types';
import { apiFetch } from './ultra-meet-config';

const THEME_CACHE_KEY = 'theme-cache';
const THEME_CACHE_DURATION = 3600000; // 1 hour in milliseconds
const MAX_CACHE_SIZE = 50; // Maximum number of cached themes

export interface ThemeCacheEntry {
    themeName: string;
    themeData: IClientInfo;
    timestamp: number;
    switchAllowed: boolean;
    availableThemes: string[];
}

export interface ThemeCache {
    [key: string]: ThemeCacheEntry;
}

/**
 * Get theme from local cache
 */
export function getCachedTheme(themeName?: string): ThemeCacheEntry | null {
    if (typeof window === 'undefined') {
        return null; // SSR guard
    }

    try {
        const cacheString = localStorage.getItem(THEME_CACHE_KEY);
        if (!cacheString) {
            return null;
        }

        const cache: ThemeCache = JSON.parse(cacheString);
        const cacheKey = themeName || 'default';
        const entry = cache[cacheKey];

        if (!entry) {
            return null;
        }

        // Check if cache entry is still valid (within 1 hour)
        const now = Date.now();
        const cacheAge = now - entry.timestamp;
        const isValid = cacheAge < THEME_CACHE_DURATION;

        if (isValid) {
            return entry;
        } else {
            // Cache expired, remove this entry
            delete cache[cacheKey];
            localStorage.setItem(THEME_CACHE_KEY, JSON.stringify(cache));
            return null;
        }
    } catch (error) {
        console.warn('Failed to read theme cache:', error);
        // Clear corrupted cache
        localStorage.removeItem(THEME_CACHE_KEY);
        return null;
    }
}

/**
 * Store theme in local cache
 */
export function setCachedTheme(themeName: string, themeData: IClientInfo, switchAllowed: boolean, availableThemes: string[]): void {
    if (typeof window === 'undefined') {
        return; // SSR guard
    }

    try {
        // Get existing cache or create new one
        const cacheString = localStorage.getItem(THEME_CACHE_KEY);
        let cache: ThemeCache = {};

        if (cacheString) {
            cache = JSON.parse(cacheString);
        }

        // Clean up expired entries and enforce size limit
        cleanupCache(cache);

        // Add new entry
        const cacheKey = themeName || 'default';
        cache[cacheKey] = {
            themeName,
            themeData,
            timestamp: Date.now(),
            switchAllowed,
            availableThemes
        };

        // Save to localStorage
        localStorage.setItem(THEME_CACHE_KEY, JSON.stringify(cache));
    } catch (error) {
        console.warn('Failed to save theme cache:', error);
        // If storage is full, try to clear old entries
        try {
            cleanupCache({});
            localStorage.setItem(
                THEME_CACHE_KEY,
                JSON.stringify({
                    [themeName || 'default']: {
                        themeName,
                        themeData,
                        timestamp: Date.now(),
                        switchAllowed,
                        availableThemes
                    }
                })
            );
        } catch (retryError) {
            console.error('Failed to save theme cache after cleanup:', retryError);
        }
    }
}

/**
 * Clean up expired cache entries and enforce size limit
 */
function cleanupCache(cache: ThemeCache): void {
    const now = Date.now();
    const entries = Object.entries(cache);

    // Remove expired entries
    entries.forEach(([key, entry]) => {
        const cacheAge = now - entry.timestamp;
        if (cacheAge >= THEME_CACHE_DURATION) {
            delete cache[key];
        }
    });

    // Enforce size limit by removing oldest entries
    const remainingEntries = Object.entries(cache);
    if (remainingEntries.length > MAX_CACHE_SIZE) {
        remainingEntries
            .sort(([, a], [, b]) => a.timestamp - b.timestamp) // Sort by timestamp (oldest first)
            .slice(0, remainingEntries.length - MAX_CACHE_SIZE) // Take oldest entries to remove
            .forEach(([key]) => delete cache[key]);
    }
}

/**
 * Clear all cached themes
 */
export function clearThemeCache(): void {
    if (typeof window === 'undefined') {
        return; // SSR guard
    }

    try {
        localStorage.removeItem(THEME_CACHE_KEY);
    } catch (error) {
        console.warn('Failed to clear theme cache:', error);
    }
}

/**
 * Get all cached theme names
 */
export function getCachedThemeNames(): string[] {
    if (typeof window === 'undefined') {
        return []; // SSR guard
    }

    try {
        const cacheString = localStorage.getItem(THEME_CACHE_KEY);
        if (!cacheString) {
            return [];
        }

        const cache: ThemeCache = JSON.parse(cacheString);
        const now = Date.now();

        return Object.entries(cache)
            .filter(([, entry]) => {
                const cacheAge = now - entry.timestamp;
                return cacheAge < THEME_CACHE_DURATION;
            })
            .map(([, entry]) => entry.themeName);
    } catch (error) {
        console.warn('Failed to get cached theme names:', error);
        return [];
    }
}

/**
 * Check if a theme is cached and valid
 */
export function isThemeCached(themeName?: string): boolean {
    return getCachedTheme(themeName) !== null;
}

/**
 * Get cache statistics for debugging
 */
export function getCacheStats(): {
    totalEntries: number;
    validEntries: number;
    expiredEntries: number;
    cacheSize: number;
} {
    if (typeof window === 'undefined') {
        return { totalEntries: 0, validEntries: 0, expiredEntries: 0, cacheSize: 0 };
    }

    try {
        const cacheString = localStorage.getItem(THEME_CACHE_KEY);
        if (!cacheString) {
            return { totalEntries: 0, validEntries: 0, expiredEntries: 0, cacheSize: 0 };
        }

        const cache: ThemeCache = JSON.parse(cacheString);
        const now = Date.now();
        const entries = Object.values(cache);

        const validEntries = entries.filter((entry) => {
            const cacheAge = now - entry.timestamp;
            return cacheAge < THEME_CACHE_DURATION;
        }).length;

        return {
            totalEntries: entries.length,
            validEntries,
            expiredEntries: entries.length - validEntries,
            cacheSize: cacheString.length
        };
    } catch (error) {
        console.warn('Failed to get cache stats:', error);
        return { totalEntries: 0, validEntries: 0, expiredEntries: 0, cacheSize: 0 };
    }
}

/**
 * Background cache refresh - call this periodically to keep cache fresh
 */
export async function refreshThemeCache(themeName?: string, apiEndpoint: string = '/api/theme'): Promise<void> {
    if (typeof window === 'undefined') {
        return; // SSR guard
    }

    try {
        const url = themeName ? `${apiEndpoint}?name=${themeName}` : apiEndpoint;
        const response = await apiFetch(url);

        if (!response.ok) {
            throw new Error(`Failed to refresh theme cache: ${response.status}`);
        }

        const data = await response.json();

        if (data.result === 'success' && data.theme) {
            setCachedTheme(themeName || 'default', data.theme, data.switchAllowed || false, data.availableThemes || []);
        }
    } catch (error) {
        console.warn('Failed to refresh theme cache:', error);
    }
}
